import React, { useEffect, useRef, useState } from "react";
import { Box, Button, Collapse, TextField } from "@mui/material";
import { useUpdateEmailMutation } from "rdx/modules/emailVerification/endpoints";

type EditEmailInlinePropsT = {
  currentEmail: string;
  authFormat?: string;
  onSuccess: (newEmail: string) => void;
};

const EMAIL_REGEX = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

const textButtonSx = {
  fontFamily: "'Barlow', sans-serif",
  color: "rgba(255,255,255,0.5)",
  textTransform: "none",
  fontSize: 14,
  "&:hover": {
    color: "#20C2F1",
    backgroundColor: "transparent",
  },
} as const;

const submitButtonSx = {
  background: "linear-gradient(90deg, #f97316, #fb923c)",
  borderRadius: "50px",
  py: 1.25,
  px: 3,
  minWidth: 180,
  fontSize: "0.9rem",
  fontWeight: 700,
  fontFamily: "'Barlow', sans-serif",
  textTransform: "none",
  boxShadow: "0 4px 24px rgba(249,115,22,0.35)",
  "&:hover": { background: "linear-gradient(90deg, #ea6c0a, #f97316)" },
  "&.Mui-disabled": {
    background: "rgba(249,115,22,0.3)",
    color: "rgba(255,255,255,0.4)",
  },
} as const;

const mapServerError = (msg: string): string => {
  const lower = msg.toLowerCase();
  if (msg === "oauth_email_immutable")
    return "Your email is managed by your sign-in provider and can't be changed here.";
  if (msg === "email_unchanged") return "That's already your email.";
  if (lower.includes("too many email changes")) return msg;
  if (lower.includes("already verified")) return "Your email is already verified.";
  if (lower.includes("taken") || lower.includes("already in use") || lower.includes("already been taken")) {
    return "That email is already in use.";
  }
  return "Couldn't update your email. Please try again.";
};

const EditEmailInline = ({ currentEmail, authFormat, onSuccess }: EditEmailInlinePropsT) => {
  const [updateEmail, { isLoading }] = useUpdateEmailMutation();
  const [expanded, setExpanded] = useState(false);
  const [value, setValue] = useState(currentEmail);
  const [localError, setLocalError] = useState("");
  const inputRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    setValue(currentEmail);
  }, [currentEmail]);

  useEffect(() => {
    if (!expanded || !inputRef.current) return;
    inputRef.current.focus();
    inputRef.current.select();
  }, [expanded]);

  if (authFormat !== "password" && authFormat !== "any_auth") {
    return null;
  }

  const handleOpen = () => {
    setValue(currentEmail);
    setLocalError("");
    setExpanded(true);
  };

  const handleCancel = () => {
    setExpanded(false);
    setValue(currentEmail);
    setLocalError("");
  };

  const handleSubmit = async () => {
    const trimmed = value.trim().toLowerCase();
    if (!EMAIL_REGEX.test(trimmed)) {
      setLocalError("Please enter a valid email address.");
      return;
    }
    if (trimmed === currentEmail.trim().toLowerCase()) {
      setLocalError("That's already your email.");
      return;
    }
    setLocalError("");
    try {
      await updateEmail({ email: trimmed }).unwrap();
      onSuccess(trimmed);
      setExpanded(false);
    } catch (err) {
      const fetchError = err as { data?: { error?: { message?: string }; message?: string } };
      const serverMessage = fetchError?.data?.error?.message || fetchError?.data?.message || "";
      setLocalError(mapServerError(serverMessage));
    }
  };

  const handleKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === "Enter") {
      handleSubmit();
    }
  };

  const isSubmitDisabled =
    isLoading || !value.trim() || value.trim().toLowerCase() === currentEmail.trim().toLowerCase();

  return (
    <Box sx={{ display: "flex", flexDirection: "column", alignItems: "center", width: "100%" }}>
      {!expanded && (
        <Button variant="text" onClick={handleOpen} sx={textButtonSx}>
          Wrong email? Update it
        </Button>
      )}
      <Collapse in={expanded} unmountOnExit>
        <Box
          sx={{
            display: "flex",
            flexDirection: "column",
            alignItems: "center",
            gap: 1.5,
            mt: 0.5,
            width: "100%",
          }}
        >
          <TextField
            inputRef={inputRef}
            type="email"
            autoComplete="email"
            inputMode="email"
            value={value}
            onChange={(e) => {
              setValue(e.target.value);
              if (localError) setLocalError("");
            }}
            onKeyDown={handleKeyDown}
            error={!!localError}
            helperText={localError || " "}
            size="small"
            fullWidth
            sx={{
              "& .MuiInputBase-root": {
                backgroundColor: "rgba(255,255,255,0.06)",
                borderRadius: "8px",
                color: "white",
              },
              "& .MuiInputBase-input": {
                color: "white",
                fontFamily: "'Barlow', sans-serif",
                fontSize: 14,
              },
              "& .MuiOutlinedInput-notchedOutline": {
                borderColor: "rgba(255,255,255,0.2)",
              },
              "& .MuiInputBase-root:hover .MuiOutlinedInput-notchedOutline": {
                borderColor: "rgba(255,255,255,0.4)",
              },
              "& .MuiInputBase-root.Mui-focused .MuiOutlinedInput-notchedOutline": {
                borderColor: "#20C2F1",
              },
              "& .MuiFormHelperText-root": {
                fontFamily: "'Barlow', sans-serif",
                fontSize: 12,
                ml: 0,
              },
            }}
          />
          <Box sx={{ display: "flex", alignItems: "center", gap: 1.5, flexWrap: "wrap", justifyContent: "center" }}>
            <Button variant="contained" onClick={handleSubmit} disabled={isSubmitDisabled} sx={submitButtonSx}>
              {isLoading ? "Sending..." : "Send to new address"}
            </Button>
            <Button variant="text" onClick={handleCancel} sx={textButtonSx}>
              Cancel
            </Button>
          </Box>
        </Box>
      </Collapse>
    </Box>
  );
};

export default EditEmailInline;
